import { STARWEAVE_NODES } from '~/data/starweave'
import { RELIC_POOL } from '~/data/relics'
import { ELEMENTS, type RunState, type Stats, type RewardOffer } from '~/types/game'
import type { StarweaveRunState } from '~/types/starweave'
import { freshStarweaveNode, starweaveNodeKey } from '~/utils/starweaveRun'

const nodeIds = new Set(STARWEAVE_NODES.map(node => node.id))
const relicIds = new Set(RELIC_POOL.map(relic => relic.id))
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)
const count = (value: unknown, max: number) => typeof value === 'number' && Number.isFinite(value)
  ? Math.min(max, Math.max(0, Math.floor(value))) : 0

/** Restore run effects only; account ownership and wallet value never come from a run save. */
export function normalizeStarweave(run: RunState): void {
  const raw: unknown = run.starweave
  if (!record(raw) || !Array.isArray(raw.owned) || run.mode === 'daily' || typeof run.puzzleLevel === 'number') {
    delete run.starweave
    return
  }
  const restored: StarweaveRunState = {
    owned: [...new Set(raw.owned.filter((id): id is string => typeof id === 'string' && nodeIds.has(id)))],
  }
  const rosterIds = new Set(run.roster.map(creature => creature.id))
  if (typeof raw.firstDraftedCreatureId === 'string' && rosterIds.has(raw.firstDraftedCreatureId))
    restored.firstDraftedCreatureId = raw.firstDraftedCreatureId
  for (const key of ['skillRerollUsed', 'secondDawnUsed'] as const)
    if (raw[key] === true) restored[key] = true
  // 🎓 v7.69: the double lesson is per act now; a pre-v7.69 save that had spent its one counts as this act's.
  if (raw.curriculumUsed === true) restored.curriculumAct = run.act
  for (const key of ['relicRerollAct', 'collectorAct', 'cashbackAct', 'restBonusAct', 'recruitBonusAct', 'lastReserveAct', 'curriculumAct'] as const) {
    const value = raw[key]
    if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0 && value <= run.act)
      restored[key] = value
  }
  if (record(raw.elementalBonuses)) {
    const bonuses: Record<string, Partial<Stats>> = {}
    for (const [id, value] of Object.entries(raw.elementalBonuses)) {
      if (!rosterIds.has(id) || !record(value)) continue
      const points: Partial<Stats> = {}
      for (const element of ELEMENTS) {
        const amount = count(value[element], 1_000_000)
        if (amount) points[element] = amount
      }
      if (Object.keys(points).length) bonuses[id] = points
    }
    if (Object.keys(bonuses).length) restored.elementalBonuses = bonuses
  }
  const current = raw.node
  if (record(current) && current.key === starweaveNodeKey(run)) {
    const node = freshStarweaveNode(current.key as string)
    for (const key of ['momentumIds', 'courierIds'] as const)
      if (Array.isArray(current[key])) node[key] = [...new Set(current[key].filter((id): id is string => typeof id === 'string' && rosterIds.has(id)))]
    if (typeof current.readyCreatureId === 'string' && rosterIds.has(current.readyCreatureId))
      node.readyCreatureId = current.readyCreatureId
    node.riskWins = count(current.riskWins, 3)
    node.mentorshipXp = count(current.mentorshipXp, 9)
    for (const key of ['pairedUsed', 'failureXpUsed', 'failureReductionUsed', 'goldRefreshUsed', 'windfallUsed', 'hadFailure', 'regroupUsed', 'homewardUsed'] as const)
      node[key] = current[key] === true
    restored.node = node
  }
  const pending = raw.collectorPending
  if (restored.owned.includes('nova-discovery') && restored.collectorAct !== run.act && record(pending)
    && (pending.source === 'starting' || pending.source === 'reward') && Array.isArray(pending.offers)) {
    const available = pending.source === 'starting' && run.phase === 'bonus' && run.draft
      ? new Set(run.draft.bonusOptions)
      : pending.source === 'reward' ? new Set(run.pendingRewards?.filter(offer => offer.pool === 'relic').map(offer => offer.defId))
        : new Set<string>()
    const seen = new Set<string>()
    const offers = pending.offers.filter((offer): offer is RewardOffer => {
      if (!record(offer) || typeof offer.id !== 'string' || offer.pool !== 'relic'
        || typeof offer.defId !== 'string' || !relicIds.has(offer.defId)
        || !available.has(offer.defId) || run.relics.includes(offer.defId) || seen.has(offer.defId)) return false
      seen.add(offer.defId)
      return true
    }).map(offer => ({ ...offer }))
    if (offers.length) restored.collectorPending = { source: pending.source, offers }
  }
  run.starweave = restored
  for (const trip of run.encounter?.returnTrips ?? [])
    if (trip.starweaveFailed !== true) delete trip.starweaveFailed
}
